import { useState, useEffect } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import {
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Breadcrumb,
  Alert,
} from 'react-bootstrap'
import useUserDetail from 'hooks/use-user-detail'
import useUserChats from 'hooks/use-user-chats'
import useChatMessages from 'hooks/use-chat-messages'
import useGetAppleIdByUserId from 'hooks/use-get-apple-id-by-user-id'
import { useAudioContext } from 'contexts/AudioContext'
import moment from 'moment'
import ChatsListCard from 'components/user-messages-page/chats-list-card'
import ConversationsCard from 'components/user-messages-page/conversations-card'
import { Chat, Message } from 'components/user-messages-page/types'
import { shouldBlockGhostMessages } from 'utils/ghost-access'

const UserMessagesPage = () => {
  const { uuid } = useParams<{ uuid: string }>()
  const history = useHistory()
  const { pauseCurrentAudio } = useAudioContext()
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [allChats, setAllChats] = useState<Chat[]>([])
  const [hasMoreChats, setHasMoreChats] = useState(true)
  const [filterType, setFilterType] = useState<
    'all' | 'no_response' | 'response' | 'issues'
  >('all')

  const { data: userData, isFetching } = useUserDetail(uuid)
  const user = userData?.user

  // Fetch Apple ID details for correct phone number (same as user-phone-details-card)
  const { data: appleIdDetails } = useGetAppleIdByUserId(user?.id || '')

  // Fetch user chats with pagination and filter
  const { data: chatsData, isLoading: chatsLoading } = useUserChats(
    user?.id || '',
    {
      page: currentPage,
      perPage: 50,
      filter: filterType,
    }
  )

  // Fetch messages for selected chat
  const { data: messagesData, isLoading: messagesLoading } = useChatMessages(
    selectedChatId || ''
  )

  // Reset pagination when filter changes
  useEffect(() => {
    setCurrentPage(1)
    setAllChats([])
    setSelectedChatId(null) // Also clear selected chat when filter changes
  }, [filterType])

  // Handle pagination state management
  useEffect(() => {
    if (chatsData?.data?.chats) {
      const newChats = chatsData.data.chats
      // Get pagination data from the correct location (root level based on API response)
      const totalPages =
        chatsData?.total_pages || chatsData?.number_of_pages || 1

      if (currentPage === 1) {
        // Page 1: Replace all chats (either first load or after filter change)
        setAllChats(newChats)
      } else {
        // Page 2+: Append to existing chats
        setAllChats((prev) => {
          // Prevent duplicates by checking if any of the new chats already exist
          const existingIds = new Set(prev.map((chat: Chat) => chat.id))
          const uniqueNewChats = newChats.filter(
            (chat: Chat) => !existingIds.has(chat.id)
          )
          return [...prev, ...uniqueNewChats]
        })
      }

      // Check if there are more pages
      setHasMoreChats(currentPage < totalPages)
    }
  }, [chatsData])

  // Stop audio when selectedChatId changes or when component unmounts
  useEffect(() => {
    return () => {
      pauseCurrentAudio()
    }
  }, [selectedChatId, pauseCurrentAudio])

  const chats: Chat[] = allChats
  const messages: Message[] = messagesData?.data?.chat?.messages || []

  const getOtherPhoneNumber = (chat: Chat) => {
    const otherHandle = chat.chat_handles?.find((handle) => !handle.is_me)
    return otherHandle?.identifier || ''
  }

  const handleBackToUser = () => {
    history.push(`/user/${uuid}`)
  }

  const handleBackToChats = () => {
    setSelectedChatId(null)
  }

  const handleLoadMoreChats = () => {
    if (hasMoreChats && !chatsLoading) {
      setCurrentPage((prev) => prev + 1)
    }
  }

  const formatTimestamp = (timestamp: string) => {
    return moment(timestamp).format('MMM D, YYYY h:mm A')
  }
  const selectedChat =
    chats.find((c) => c.id.toString() === selectedChatId) || null

  // Check if organization has redacted message history
  const redactMessageHistory = shouldBlockGhostMessages(
    user?.organization?.redact_message_history,
    user?.organization?.organization_subscription?.customer_product_type
  )

  // Block access and auto-redirect if redact_message_history is enabled
  useEffect(() => {
    if (redactMessageHistory && !isFetching) {
      // Redirect back to user detail page after a short delay to show the message
      const timer = setTimeout(() => {
        history.replace(`/user/${uuid}`)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [redactMessageHistory, isFetching, uuid, history])

  if (isFetching) {
    return (
      <Container
        className="text-center"
        style={{ paddingTop: 'clamp(40px, 15vw, 100px)' }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    )
  }

  const breadcrumbContent = (
    <Breadcrumb>
      <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
      <Breadcrumb.Item href="/users">Users</Breadcrumb.Item>
      <Breadcrumb.Item href={`/user/${uuid}`}>
        {user?.name || 'User Detail'}
      </Breadcrumb.Item>
      <Breadcrumb.Item active>View Messages</Breadcrumb.Item>
    </Breadcrumb>
  )

  const backButton = (
    <Button
      variant="success"
      onClick={handleBackToUser}
      style={{
        marginRight: 'clamp(8px, 2vw, 16px)',
        padding: 'clamp(6px, 1.5vw, 10px)',
        fontSize: 'clamp(14px, 3vw, 16px)',
      }}
    >
      <ion-icon name="chevron-back" />
    </Button>
  )

  // Show blocked message if redact_message_history is enabled
  if (redactMessageHistory) {
    return (
      <Container className="mb-4">
        {breadcrumbContent}

        <div className="d-flex align-items-center mb-2">
          {backButton}
          <h3 className="mb-0" style={{ fontSize: 'clamp(18px, 4vw, 24px)' }}>
            {user?.name || 'User Messages'}
          </h3>
        </div>

        <Alert variant="danger" className="mt-3">
          <Alert.Heading>Access Denied</Alert.Heading>
          <p>
            Message history is redacted for this organization due to active
            partner integration. Access to chat messages is restricted.
          </p>
          <hr />
          <p className="mb-0">
            You will be redirected back to the user details page in a moment...
          </p>
        </Alert>
      </Container>
    )
  }
  return (
    <Container className="mb-4">
      {breadcrumbContent}

      <div className="d-flex align-items-center mb-2">
        {backButton}
        <div>
          <h3 className="mb-0" style={{ fontSize: 'clamp(18px, 4vw, 24px)' }}>
            {user?.name || 'User Messages'}
          </h3>
          {(appleIdDetails?.phone_number || user?.worknumber?.number) && (
            <h4
              className="mb-0"
              style={{ fontSize: 'clamp(16px, 3.5vw, 20px)' }}
            >
              {appleIdDetails?.phone_number || user?.worknumber?.number}
            </h4>
          )}
        </div>
      </div>

      <Row style={{ margin: '0 clamp(-8px, -2vw, -12px)' }}>
        <Col
          xs={12}
          lg={4}
          className={selectedChatId ? 'd-none d-lg-block' : ''}
          style={{ padding: '0 clamp(4px, 1vw, 8px)' }}
        >
          <ChatsListCard
            chats={chats}
            chatsLoading={chatsLoading}
            onSelectChat={setSelectedChatId}
            hasMoreChats={hasMoreChats}
            onLoadMoreChats={handleLoadMoreChats}
            selectedChatId={selectedChatId}
            filterType={filterType}
            onFilterChange={setFilterType}
          />
        </Col>

        <Col
          xs={12}
          lg={8}
          className={!selectedChatId ? 'd-none d-lg-block' : ''}
          style={{ padding: '0 clamp(4px, 1vw, 8px)' }}
        >
          <ConversationsCard
            selectedChat={selectedChat}
            messages={messages}
            messagesLoading={messagesLoading}
            getOtherPhoneNumber={getOtherPhoneNumber}
            formatTimestamp={formatTimestamp}
            onBack={handleBackToChats}
          />
        </Col>
      </Row>
    </Container>
  )
}

export default UserMessagesPage
